import type { Constraint } from "@/types/schemas/create-table-query";
import {
  columnInputSchema,
  carrierTableBuilderSchema,
} from "@/types/schemas/create-table-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/Form";
import { Input } from "@/components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/Tooltip";
import { Icons } from "@/components/ui/Icons";
import { Checkbox } from "@/components/ui/Checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import { Button } from "@/components/ui/Button";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/Calendar";
import { Portal } from "./Portal";
import { cn, getKeyByValue } from "@/util/misc";

export const sqlDataTypes = [
  "VARCHAR(255)",
  "VARCHAR(5)",
  "INT",
  "TEXT",
  "DATE",
  "BOOLEAN",
  "DECIMAL(10, 2)",
  "TIMESTAMP",
  "DATETIME",
  "BIGINT",
  "SMALLINT",
] as const;

export const userLabelToSqlType = {
  "Short Text": "VARCHAR(255)",
  "Long Text": "TEXT",
  "Whole Number": "INT",
  "Decimal Number": "DECIMAL(10, 2)",
  Date: "DATE",
  "Date and Time": "DATETIME",
} as const;

export const requiredFicMappings = [
  "policy_number",
  "agent_name",
  "member_name_and_last_name",
  "state",
  "rate",
  "number_of_members",
  "policy_effective_date",
  "commission_date",
  "commission_amount",
  "description",
  "agent_matcher",
  "override",
  "lives",
] as const;

type RequiredFicMapping = (typeof requiredFicMappings)[number];
type SQLDatatype = (typeof sqlDataTypes)[number];

type FicMapping = {
  value: RequiredFicMapping;
  label: string;
  description: string;
  presets: {
    dataType: SQLDatatype;
    isNullable: boolean;
    constraints?: Constraint[];
  };
};

export type TableBuilderData = {
  columns: z.infer<typeof carrierTableBuilderSchema>["columns"];
  tableName: string;
};

type Props = {
  onSubmit: (data: TableBuilderData) => void;
  isPending: boolean;
  mappings: FicMapping[];
  type: "carrier" | "member-template";
};

export function CreateTableQueryBuilder({
  onSubmit,
  isPending,
  mappings,
  type,
}: Props) {
  const form = useForm<
    z.input<typeof carrierTableBuilderSchema>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    z.output<typeof carrierTableBuilderSchema>
  >({
    resolver: zodResolver(carrierTableBuilderSchema),
    defaultValues: {
      tableName: "",
      columns: [],
    },
    mode: "onSubmit",
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "columns",
  });

  // Separate form for inputting a single column's details
  const columnInputForm = useForm<
    z.input<typeof columnInputSchema>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    z.output<typeof columnInputSchema>
  >({
    resolver: zodResolver(columnInputSchema),
    defaultValues: {
      columnName: "",
      mapping: "",
      dataType: "",
      isNullable: false,
      constraints: [],
    },
    mode: "onChange",
  });

  const handleAddColumn = (data: z.infer<typeof columnInputSchema>) => {
    append({ id: "1", ...data });
    columnInputForm.reset();
  };

  const handleFormSubmit = (
    data: z.infer<typeof carrierTableBuilderSchema>,
  ) => {
    onSubmit({
      columns: data.columns,
      tableName:
        type === "member-template" ? "MEMBER_TEMPLATE" : data.tableName,
    });
  };

  return (
    <div>
      <div
        className={cn(
          "mb-8 p-6 border-gray-200 rounded-lg bg-white",
          type !== "carrier" && "border shadow-sm",
        )}
      >
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleFormSubmit)}
            className="space-y-8"
          >
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="tableName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {type === "carrier" ? "Carrier Name" : ""}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., HealthCare"
                        {...field}
                        className={`p-2 border border-gray-300 rounded-md ${type === "member-template" ? "hidden" : ""}`}
                        value={
                          type === "member-template"
                            ? "MEMBER_TEMPLATE"
                            : field.value
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={columnInputForm.control}
                name="columnName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Column Name</FormLabel>
                    <FormControl>
                      <Input
                        id="columnNameInput"
                        placeholder="e.g., ID"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={columnInputForm.control}
                name="mapping"
                render={({ field }) => {
                  const selectedMapping = mappings.find(
                    (m) => m.value === field.value,
                  );

                  return (
                    <FormItem>
                      <FormLabel>Mapping</FormLabel>

                      <div className="flex items-center space-x-2">
                        <Select
                          onValueChange={(value) => {
                            if (value === "__none__") {
                              field.onChange("");
                              columnInputForm.resetField("constraints");
                              columnInputForm.setValue("dataType", "");
                              columnInputForm.setValue("isNullable", false);
                              return;
                            }

                            field.onChange(value);
                            columnInputForm.resetField("constraints");
                            const selected = mappings.find(
                              (m) => m.value === value,
                            );

                            if (selected?.presets) {
                              columnInputForm.setValue(
                                "dataType",
                                selected.presets.dataType,
                              );
                              columnInputForm.setValue(
                                "isNullable",
                                selected.presets.isNullable,
                              );
                              if (selected.presets.constraints) {
                                columnInputForm.setValue(
                                  "constraints",
                                  selected.presets.constraints,
                                );
                              }
                            }
                          }}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select data type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem key="-1" value="__none__">
                              Select data type
                            </SelectItem>
                            {mappings
                              .filter(
                                (m) =>
                                  !fields.some((f) => f.mapping === m.value),
                              )
                              .map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>

                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                className={`cursor-pointer ${field.value === "" ? "hidden" : ""}`}
                              >
                                <Icons.info />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent
                              side="right"
                              className="max-w-xs text-sm"
                            >
                              <p>{selectedMapping?.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>

                      <FormDescription>
                        Mappings help link key values in your data to specific
                        rules or categories. This lets you easily filter, sort,
                        and summarize important information throughout the
                        system.
                      </FormDescription>
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={columnInputForm.control}
                name="dataType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data Type</FormLabel>
                    <Select
                      onValueChange={(e) => {
                        field.onChange(e);
                        columnInputForm.resetField("constraints");
                      }}
                      value={field.value}
                      disabled={!!columnInputForm.watch("mapping")}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select data type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.keys(userLabelToSqlType).map((userType) => (
                          <SelectItem
                            key={userType}
                            value={
                              userLabelToSqlType[
                                userType as keyof typeof userLabelToSqlType
                              ]
                            }
                          >
                            {userType}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={columnInputForm.control}
                name="isNullable"
                render={({ field }) => (
                  <FormItem
                    // eslint-disable-next-line no-extra-boolean-cast
                    className={`${!!columnInputForm.watch("mapping") ? "opacity-60 " : ""} flex flex-row items-center space-x-3 space-y-0 rounded-md border p-4 shadow-sm bg-gray-50`}
                  >
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        id="isNullableCheckbox"
                        disabled={!!columnInputForm.watch("mapping")}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel
                        htmlFor="isNullableCheckbox"
                        className="cursor-pointer"
                      >
                        Is Optional?
                      </FormLabel>
                      <FormDescription>
                        When checked, this column can contain empty cells.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={columnInputForm.control}
                name="constraints"
                render={({ field }) => {
                  const dataType = columnInputForm
                    .watch("dataType")
                    .toLowerCase();

                  return (
                    <FormItem>
                      <FormLabel>Constraints</FormLabel>
                      <div className="space-y-3">
                        {(field.value ?? []).map((constraint, index) => {
                          const updateConstraint = (value: string | number) => {
                            const updated = [...(field.value || [])];
                            updated[index] = {
                              ...updated[index],
                              value,
                            };
                            field.onChange(updated);
                          };

                          return (
                            <div
                              key={index}
                              className="flex gap-2 items-center"
                            >
                              <select
                                className="border rounded px-2 py-1"
                                value={constraint.type}
                                onChange={(e) => {
                                  const updated = [...(field.value || [])];
                                  updated[index] = {
                                    ...updated[index],
                                    // @ts-expect-error it is safe the conversion between literals to strings
                                    type: e.target.value,
                                  };
                                  field.onChange(updated);
                                }}
                              >
                                <option value="min">Min</option>
                                <option value="max">Max</option>
                              </select>

                              {dataType === "date" ||
                              dataType === "datetime" ? (
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="outline"
                                      className="w-[200px] justify-start text-left"
                                    >
                                      {constraint.value
                                        ? format(
                                            new Date(constraint.value),
                                            dataType === "datetime"
                                              ? "PPP p"
                                              : "PPP",
                                          )
                                        : "Pick a date"}
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-auto p-0">
                                    <Calendar
                                      mode="single"
                                      selected={
                                        constraint.value
                                          ? new Date(constraint.value)
                                          : undefined
                                      }
                                      onSelect={(date) => {
                                        if (date) {
                                          updateConstraint(
                                            format(date, "yyyy-MM-dd"),
                                          );
                                        }
                                      }}
                                      initialFocus
                                    />
                                  </PopoverContent>
                                </Popover>
                              ) : (
                                <Input
                                  type="number"
                                  className="w-full"
                                  value={constraint.value}
                                  onChange={(e) => {
                                    updateConstraint(Number(e.target.value));
                                  }}
                                />
                              )}

                              <Button
                                type="button"
                                variant="ghost"
                                onClick={() => {
                                  const updated = field?.value?.filter(
                                    (_, i) => i !== index,
                                  );
                                  field.onChange(updated);
                                }}
                              >
                                <Icons.trash />
                              </Button>
                            </div>
                          );
                        })}

                        <div className="flex items-center justify-between">
                          <Button
                            type="button"
                            variant="outline"
                            disabled={!!columnInputForm.watch("mapping")}
                            onClick={() =>
                              field.onChange([
                                ...(field.value ?? []),
                                { type: "min", value: 0 },
                              ])
                            }
                          >
                            + Add Constraint
                          </Button>

                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="pointer">
                                  <Icons.info />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent
                                side="right"
                                className="max-w-xs text-sm"
                              >
                                <p>
                                  Constraints let you set rules or limits on
                                  certain fields — like requiring a value to be
                                  above or below a specific number. They help
                                  ensure your data follows the conditions you
                                  need.
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>

                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              <div className="flex justify-between">
                <Button
                  type="button"
                  onClick={columnInputForm.handleSubmit(handleAddColumn)}
                  className="w-full sm:w-auto"
                >
                  Add Column to Table
                </Button>

                <Button type="submit" disabled={isPending}>
                  {isPending ? (
                    <Icons.spinner className="animate-spin" />
                  ) : (
                    <p>
                      {type === "carrier"
                        ? "Create Carrier"
                        : "Create Template"}
                    </p>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </div>

      {fields.length > 0 && (
        <Portal
          targetId="portal-columns"
          disabled={type === "carrier" ? true : false}
        >
          <div className="space-y-4 mt-6  p-4">
            <div className="border rounded-lg divide-y bg-white">
              {fields.map((item, index) => {
                const columnName = form.getValues(
                  `columns.${index}.columnName`,
                );
                const dataType = form.getValues(`columns.${index}.dataType`);
                const isNullable = form.getValues(
                  `columns.${index}.isNullable`,
                );
                const mapping = form.getValues(`columns.${index}.mapping`);

                return (
                  <div
                    key={item.id}
                    className="flex items-start justify-between p-4 sm:items-center"
                  >
                    <div className="space-y-1 text-sm">
                      <div className="font-medium text-gray-900">
                        {columnName}
                      </div>
                      <div className="text-gray-500">
                        {getKeyByValue(userLabelToSqlType, dataType)}{" "}
                        <span
                          className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium
                    ${isNullable ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-800"}`}
                        >
                          {isNullable ? "NULLABLE" : "NOT NULL"}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {mapping ? (
                          <>
                            Mapped to: <code>{mapping}</code>
                          </>
                        ) : (
                          "No mapping"
                        )}
                      </div>
                    </div>

                    <Button
                      type="button"
                      onClick={() => remove(index)}
                      variant="ghost"
                      size="icon"
                    >
                      <Icons.trash />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}
